import React from 'react';
import { Globe, Sparkles, Palette, Rocket, Search, Smartphone, Bot, Code2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const SitesIA = () => {
  const services = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Design com IA',
      description: 'Layouts modernos e únicos criados com auxílio de inteligência artificial'
    },
    {
      icon: <Bot className="w-8 h-8" />,
      title: 'Chatbots Inteligentes',
      description: 'Atendimento automatizado 24h com IA conversacional'
    },
    {
      icon: <Search className="w-8 h-8" />,
      title: 'SEO Otimizado',
      description: 'Estrutura otimizada para aparecer no topo do Google'
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: '100% Responsivo',
      description: 'Perfeito em qualquer dispositivo: desktop, tablet ou celular'
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: 'Alta Velocidade',
      description: 'Sites ultra-rápidos para melhor experiência do usuário'
    },
    {
      icon: <Code2 className="w-8 h-8" />,
      title: 'Código Moderno',
      description: 'Tecnologias de ponta para máxima performance e segurança'
    }
  ];

  const siteTypes = [
    'Sites Institucionais',
    'Landing Pages',
    'E-commerce',
    'Portfólios',
    'Blogs',
    'Sistemas Web'
  ];

  return (
    <section id="sites-ia" className="py-20 bg-gradient-to-br from-emerald-950 via-teal-900 to-cyan-950 relative overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
        <div className="absolute top-10 right-0 w-64 sm:w-72 h-72 bg-emerald-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 left-0 w-64 sm:w-80 md:w-96 h-96 bg-teal-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-cyan-500 rounded-full filter blur-3xl animate-pulse delay-700"></div>
      </div>

      {/* Floating elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-[10%] text-emerald-500/20 animate-bounce" style={{ animationDuration: '3s' }}>
          <Globe className="w-16 h-16" />
        </div>
        <div className="absolute top-40 right-[15%] text-teal-500/20 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
          <Sparkles className="w-12 h-12" />
        </div>
        <div className="absolute bottom-32 left-[20%] text-cyan-500/20 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>
          <Code2 className="w-14 h-14" />
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-emerald-500/20 border border-emerald-400/30 rounded-full">
            <Globe className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-300 font-semibold text-sm uppercase tracking-wider">Web Design com IA</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Sites <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">Inteligentes e Personalizados</span>
          </h2>
          <p className="text-xl text-emerald-200 max-w-3xl mx-auto">
            Criamos sites modernos e profissionais utilizando Inteligência Artificial para desenvolver designs únicos que convertem visitantes em clientes
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white/5 backdrop-blur-sm border border-emerald-500/20 rounded-2xl p-6 hover:bg-white/10 hover:border-emerald-400/50 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-emerald-500/30">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
              <p className="text-emerald-200">{service.description}</p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-white/5 backdrop-blur-sm border border-emerald-500/20 rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Palette className="w-8 h-8 text-emerald-400" />
                <h3 className="text-2xl md:text-3xl font-bold text-white">Tipos de Sites que Desenvolvemos</h3>
              </div>
              <p className="text-emerald-200 mb-6">
                Do conceito ao lançamento, cuidamos de todo o processo de criação do seu site com tecnologia de ponta e design exclusivo.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {siteTypes.map((type, index) => (
                  <div key={index} className="flex items-center gap-2 text-white">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>{type}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 shadow-2xl shadow-emerald-500/30">
                <Sparkles className="w-12 h-12 text-white mx-auto mb-4" />
                <h4 className="text-2xl font-bold text-white mb-2">Transforme sua Presença Online</h4>
                <p className="text-emerald-200 mb-6">Solicite uma proposta e tenha um site profissional que destaca seu negócio</p>
                <Button
                  onClick={() => {
                    const element = document.querySelector('#contato');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  size="lg"
                  className="bg-white text-emerald-700 hover:bg-emerald-100 font-bold px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all duration-300 group"
                >
                  Quero Meu Site
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SitesIA;
